// Enclosure (cuadro) library: ready-made SVG panel frames + the geometry needed
// to place device modules exactly on their DIN rails.
//
// The SVG assets are produced by Cuadros/cuadro_gen.py. The constants below mirror
// that generator (npe=False variant) so device overlays line up with the printed
// rails to the pixel. 1 module = 17.5 mm; the SVG coordinate space IS millimetres,
// so the canvas renders devices directly in the enclosure's viewBox units.

// Imported as raw SVG and embedded as self-contained data: URIs (see svgToDataUri)
// so the frames render in print output — Chromium does not paint <image> that
// references an external SVG file when printing.
import cuadro1x12Raw from './assets/cuadro_1x12.svg?raw';
import cuadro2x12Raw from './assets/cuadro_2x12.svg?raw';
import cuadro3x12Raw from './assets/cuadro_3x12.svg?raw';
import cuadro4x12Raw from './assets/cuadro_4x12.svg?raw';
import cuadro4x16Raw from './assets/cuadro_4x16.svg?raw';

// Base64 data URI (not ;utf8 + percent-encoding, which renders on screen but is
// rejected by Chromium's print path). Self-contained so <image> prints.
const svgToDataUri = (svg: string): string => {
  const bytes = new TextEncoder().encode(svg);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:image/svg+xml;base64,${btoa(bin)}`;
};

const cuadro1x12Url = svgToDataUri(cuadro1x12Raw);
const cuadro2x12Url = svgToDataUri(cuadro2x12Raw);
const cuadro3x12Url = svgToDataUri(cuadro3x12Raw);
const cuadro4x12Url = svgToDataUri(cuadro4x12Raw);
const cuadro4x16Url = svgToDataUri(cuadro4x16Raw);

// ─── Generator constants (mirror cuadro_gen.py, npe=False) ─────────────────────
const WALL = 10;
const SIDE_PAD = 8;
const MOD_W = 17.5;      // width of one DIN module
const MARGIN_END = 80;   // vertical margin above the first / below the last rail
const PITCH = 150;       // vertical distance between rail centres

const RAIL_X0 = WALL + SIDE_PAD;             // 18 — left edge of every rail
const MODULES_PER_ROW_12 = 12;               // small/medium library enclosures
const MODULES_PER_ROW_16 = 16;               // large Solera 56 (4 × 16) enclosure

// Rendered module body height. Real DIN modules (~85mm) are taller than the rail
// clip band (33mm), so devices are drawn centred on the rail and overhang it both
// ways, which reads as a module snapped onto the rail. Fits within PITCH with margin.
const DEVICE_RENDER_H = 84;

function viewWidth(modules: number): number {
  return 2 * WALL + 2 * SIDE_PAD + modules * MOD_W;
}
function viewHeight(rows: number): number {
  return 2 * WALL + 2 * MARGIN_END + (rows - 1) * PITCH;
}
// Vertical centre of rail `rail` (0-based).
function railCenterY(rail: number): number {
  return WALL + MARGIN_END + rail * PITCH;
}

// ─── Variants (the three bundled assets) ───────────────────────────────────────

export interface EnclosureVariant {
  id: string;
  label: string;       // shown in the picker
  rows: number;        // number of DIN rails
  modulesPerRow: number;
  capacity: number;    // rows * modulesPerRow
  assetUrl: string;
}

// Ordered by capacity ascending so the auto-fit default picks the smallest that fits.
export const ENCLOSURE_VARIANTS: readonly EnclosureVariant[] = [
  { id: '1x12', label: '1 × 12', rows: 1, modulesPerRow: MODULES_PER_ROW_12, capacity: 1 * MODULES_PER_ROW_12, assetUrl: cuadro1x12Url },
  { id: '2x12', label: '2 × 12', rows: 2, modulesPerRow: MODULES_PER_ROW_12, capacity: 2 * MODULES_PER_ROW_12, assetUrl: cuadro2x12Url },
  { id: '3x12', label: '3 × 12', rows: 3, modulesPerRow: MODULES_PER_ROW_12, capacity: 3 * MODULES_PER_ROW_12, assetUrl: cuadro3x12Url },
  { id: '4x12', label: '4 × 12', rows: 4, modulesPerRow: MODULES_PER_ROW_12, capacity: 4 * MODULES_PER_ROW_12, assetUrl: cuadro4x12Url },
  { id: '4x16', label: '4 × 16', rows: 4, modulesPerRow: MODULES_PER_ROW_16, capacity: 4 * MODULES_PER_ROW_16, assetUrl: cuadro4x16Url },
];

export function enclosureById(id: string): EnclosureVariant {
  return ENCLOSURE_VARIANTS.find(v => v.id === id) ?? ENCLOSURE_VARIANTS[0];
}

// Smallest variant whose capacity holds `moduleCount`; falls back to the largest.
export function smallestEnclosureFor(moduleCount: number): EnclosureVariant {
  return (
    ENCLOSURE_VARIANTS.find(v => v.capacity >= moduleCount) ??
    ENCLOSURE_VARIANTS[ENCLOSURE_VARIANTS.length - 1]
  );
}

// ─── Geometry for a chosen variant ─────────────────────────────────────────────

export interface EnclosureGeometry {
  variant: EnclosureVariant;
  rows: number;
  modulesPerRow: number;
  modWidth: number;
  deviceHeight: number;
  viewW: number;
  viewH: number;
  // Left x of module column `col` (0-based) on any rail.
  slotX: (col: number) => number;
  // Top y of a device body on rail `rail` (0-based); body height === deviceHeight.
  deviceTopY: (rail: number) => number;
  // Map a point in viewBox coordinates to a {row, col} slot, or null if outside a module band.
  pointToSlot: (svgX: number, svgY: number) => { row: number; col: number } | null;
}

export function buildEnclosureGeometry(variant: EnclosureVariant): EnclosureGeometry {
  const { rows, modulesPerRow } = variant;
  const viewW = viewWidth(modulesPerRow);
  const viewH = viewHeight(rows);

  const slotX = (col: number) => RAIL_X0 + col * MOD_W;
  const deviceTopY = (rail: number) => railCenterY(rail) - DEVICE_RENDER_H / 2;

  const pointToSlot = (svgX: number, svgY: number): { row: number; col: number } | null => {
    for (let rail = 0; rail < rows; rail++) {
      const top = deviceTopY(rail);
      if (svgY < top || svgY > top + DEVICE_RENDER_H) continue;
      const rel = svgX - RAIL_X0;
      if (rel < 0) return null;
      const col = Math.floor(rel / MOD_W);
      if (col >= 0 && col < modulesPerRow) return { row: rail, col };
      return null;
    }
    return null;
  };

  return {
    variant,
    rows,
    modulesPerRow,
    modWidth: MOD_W,
    deviceHeight: DEVICE_RENDER_H,
    viewW,
    viewH,
    slotX,
    deviceTopY,
    pointToSlot,
  };
}
